<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '~/composables/useSnackbar'

interface Props {
  modelValue: boolean
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()
const { show: showSnackbar } = useSnackbar()

// Form state
const title = ref('')
const description = ref('')
const isSending = ref(false)

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const canSend = computed(() => {
  return title.value.trim().length > 0 &&
         description.value.trim().length >= 20 &&
         !isSending.value
})

const sendFeatureRequest = async () => {
  if (!canSend.value) return

  try {
    isSending.value = true

    await $fetch('/api/feature-request', {
      method: 'POST',
      body: {
        title: title.value.trim(),
        description: description.value.trim()
      }
    })

    showSnackbar(t('featureRequest.success'))
    resetForm()
    isOpen.value = false
  } catch (error: any) {
    console.error('Feature request error:', error)
    showSnackbar(t('featureRequest.error'))
  } finally {
    isSending.value = false
  }
}

const resetForm = () => {
  title.value = ''
  description.value = ''
}

const handleClose = () => {
  resetForm()
  isOpen.value = false
}
</script>

<template>
  <CommonDialog
    v-model="isOpen"
    :title="t('featureRequest.title')"
    size="lg"
    :closable="true"
    :footer="false"
  >
    <div class="grid grid-cols-1 gap-y-6">
      <SofaxNotice type="info">
        <div class="font-title text-base font-semibold text-ink dark:text-white">
          {{ t('featureRequest.infoTitle') }}
        </div>
        <p class="mt-1 text-sm">{{ t('featureRequest.infoDescription') }}</p>
      </SofaxNotice>

      <form class="grid grid-cols-1 gap-y-6" @submit.prevent="sendFeatureRequest">
        <SofaxInput
          id="feature-title"
          v-model="title"
          :label="t('featureRequest.titleLabel')"
          :placeholder="t('featureRequest.titlePlaceholder')"
          :maxlength="100"
          required
          :disabled="isSending"
        />

        <!-- Character count rides along in the hint so the field has no overlay -->
        <SofaxTextarea
          id="feature-description"
          v-model="description"
          :label="t('featureRequest.descriptionLabel')"
          :placeholder="t('featureRequest.descriptionPlaceholder')"
          :hint="`${t('featureRequest.descriptionHint')} (${description.length}/1000)`"
          :rows="6"
          :maxlength="1000"
          required
          :disabled="isSending"
        />

        <div class="flex flex-col-reverse gap-3 border-t border-line pt-6 sm:flex-row sm:justify-end dark:border-gray-700">
          <SofaxBtn
            :label="t('common.cancel')"
            variant="outline"
            type="button"
            class="btn-sm"
            :disabled="isSending"
            @click="handleClose"
          />
          <SofaxBtn
            :label="isSending ? t('featureRequest.sending') : t('featureRequest.send')"
            variant="accent"
            type="submit"
            class="btn-sm"
            :disabled="!canSend"
          />
        </div>
      </form>
    </div>
  </CommonDialog>
</template>
